import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import cn from 'classnames';
import first from 'lodash/first';
import last from 'lodash/last';

import ActionItem from 'Shared/components/ActionItem/ActionItem';
import DiscussionTopic from 'Shared/components/DiscussionTopic/DiscussionTopic';
import WorkspaceItemParentInfo from 'Shared/components/WorkspaceItemParentInfo/WorkspaceItemParentInfo';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { ItemType } from 'Shared/constants/types';
import { getNextOrPrevItem, PositionTypes } from 'Shared/utils/getNextOrPrevItem';
import { useHandleFocusWorkspaceItem } from 'Dashboard/components/WorkspacePage/hooks/use-handle-focus-workspace-item';

import TimelineGroupEmptyState from './TimelineGroupEmptyState/TimelineGroupEmptyState';
import './TimelineItemsTable.scss';

type Props = {
  items: Array<{
    uuid: string;
    parentUuid: string;
    workspacePath: string;
    parentText: string;
    isHighPriority: boolean;
  }>;
  listType: ItemType;
};

function TimelineItemsTable({ items, listType }: Props) {
  const dispatch = useDispatch();
  const workspaceId = useSelector(state => state.workspace.id);
  const { itemUuid } = useParams();
  const handleFocusWorkspaceItem = useHandleFocusWorkspaceItem({ workspaceId });

  const itemsWithoutHighPriority = items.filter(({ isHighPriority }) => !isHighPriority);
  const hasHighPriorityItems = Boolean(items.length - itemsWithoutHighPriority.length);
  const firstTodoUuid = itemsWithoutHighPriority.length ? first(itemsWithoutHighPriority).uuid : null;
  const lastTodoUuid = itemsWithoutHighPriority.length ? last(itemsWithoutHighPriority).uuid : null;
  const isActionitemsTable = listType === ItemType.actionItem;
  const ItemComponent = isActionitemsTable ? ActionItem : DiscussionTopic;

  const handleArrowClick = ({ uuid: currentItemUuid, operator }) => {
    const prevItemUuid = getNextOrPrevItem(items, currentItemUuid, operator);
    if (prevItemUuid) {
      handleFocusWorkspaceItem(prevItemUuid);
      dispatch(updateSelectedUuids(prevItemUuid, { isOneSelected: true }));
    }
  };

  const handleArrowUpClick = ({ uuid }) => handleArrowClick({ uuid, operator: PositionTypes.PREV });

  const handleArrowDownClick = ({ uuid }) => handleArrowClick({ uuid, operator: PositionTypes.NEXT });

  return (
    <>
      <div className="timeline-group-item-list-label font-weight-bold mt-3 mb-2">
        {isActionitemsTable ? 'Action Items' : 'Discussion Topics'}
      </div>
      {items.length ? (
        <div
          className={cn('d-flex flex-column w-100 top-level', {
            'action-item-list': isActionitemsTable,
            'discussion-topic-list': !isActionitemsTable,
          })}
        >
          {items.map(({ uuid, parentUuid, workspacePath, parentText }) => (
            <ItemComponent
              notShowAsChild
              key={uuid}
              uuid={uuid}
              forbidDrag
              onArrowUpClick={handleArrowUpClick}
              withDueDateSection
              withOwnerSection
              onArrowDownClick={handleArrowDownClick}
              isFocused={Boolean(itemUuid && itemUuid === uuid)}
              isFirst={firstTodoUuid === uuid}
              hasHighPriorityItems={hasHighPriorityItems}
              onFocusedTodoUpdate={handleFocusWorkspaceItem}
              isLast={uuid === lastTodoUuid}
              parentItemInfoComponent={
                parentUuid ? (
                  <div className="ml-4">
                    <WorkspaceItemParentInfo
                      parentUuid={parentUuid}
                      workspacePath={workspacePath}
                      parentText={parentText}
                      itemType={listType}
                    />
                  </div>
                ) : null
              }
            />
          ))}
        </div>
      ) : (
        <TimelineGroupEmptyState text={isActionitemsTable ? 'No action items' : 'No discussion topics'} />
      )}
    </>
  );
}

export default TimelineItemsTable;
